import { ServerStatistics } from '@amzn/awscat-portfolio-assessment-types';
import { Alert, Box, SpaceBetween } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';

import { constants } from '../../../common/Localization';
import { format } from '../../../common/util';
import { INDUSTRIAL_AVERAGE_USAGE } from './constants';

interface Props {
    serverStatistics: ServerStatistics;
}

function NegativeSavingsAlert({ serverStatistics }: Props): JSX.Element {
    const [ visible, setVisible ] = useState(false);
    const [ showServerUptime, setShowServerUptime ] = useState(false);
    const [ showAvgCpu, setShowAvgCpu ] = useState(false);
    const [ showPeakCpu, setShowPeakCpu ] = useState(false);
    const [ showAvgRam, setShowAvgRam ] = useState(false);
    const [ showPeakRam, setShowPeakRam ] = useState(false);

    useEffect(() => {
        const showServerUptimeMsg = 
            serverStatistics.serverUptime > Math.min(INDUSTRIAL_AVERAGE_USAGE.NON_PROD_SERVER_UPTIME_PCT, INDUSTRIAL_AVERAGE_USAGE.SERVER_UPTIME_PCT);
        setShowServerUptime(showServerUptimeMsg);
        const showAvgCPUMsg = serverStatistics.avgCpuUtilization > INDUSTRIAL_AVERAGE_USAGE.AVG_CPU_UTILIZATION_PCT;
        setShowAvgCpu(showAvgCPUMsg);
        const showPeakCPUMsg = serverStatistics.peakCpuUtilization > INDUSTRIAL_AVERAGE_USAGE.PEAK_CPU_UTILIZATION_PCT;
        setShowPeakCpu(showPeakCPUMsg);
        const showAvgRamMsg = serverStatistics.avgRamUtilization > INDUSTRIAL_AVERAGE_USAGE.AVG_RAM_UTILIZATION_PCT;
        setShowAvgRam(showAvgRamMsg);
        const showPeakRamMsg = serverStatistics.peakRamUtilization > INDUSTRIAL_AVERAGE_USAGE.PEAK_RAM_UTILIZATION_PCT;
        setShowPeakRam(showPeakRamMsg);
        setVisible(showServerUptimeMsg || showAvgCPUMsg || showPeakCPUMsg || showAvgRamMsg || showPeakRamMsg);
    }, [ serverStatistics ]);

    const content = (
        <SpaceBetween size='xxxs'>
            {showServerUptime && (
                <Box data-testid='server-uptime'>
                    {format(constants.NEGATIVE_SAVING_SERVER_UPTIME, [
                        INDUSTRIAL_AVERAGE_USAGE.NON_PROD_SERVER_UPTIME_PCT.toString(),
                        INDUSTRIAL_AVERAGE_USAGE.SERVER_UPTIME_PCT.toString(),
                        serverStatistics.serverUptime.toString()
                    ])}
                </Box>
            )}
            {showAvgCpu && (
                <Box data-testid='avg-cpu'>
                    {format(constants.NEGATIVE_SAVING_AVG_CPU, [
                        INDUSTRIAL_AVERAGE_USAGE.AVG_CPU_UTILIZATION_PCT.toString(), serverStatistics.avgCpuUtilization.toString()
                    ])}
                </Box>
            )}
            {showPeakCpu && (
                <Box data-testid='peak-cpu'>
                    {format(constants.NEGATIVE_SAVING_PEAK_CPU, [
                        INDUSTRIAL_AVERAGE_USAGE.PEAK_CPU_UTILIZATION_PCT.toString(), serverStatistics.peakCpuUtilization.toString()
                    ])}
                </Box>
            )}
            {showAvgRam && (
                <Box data-testid='avg-ram'>
                    {format(constants.NEGATIVE_SAVING_AVG_RAM, [
                        INDUSTRIAL_AVERAGE_USAGE.AVG_RAM_UTILIZATION_PCT.toString(), serverStatistics.avgRamUtilization.toString()
                    ])}
                </Box>
            )}
            {showPeakRam && (
                <Box data-testid='peak-ram'>
                    {format(constants.NEGATIVE_SAVING_PEAK_RAM, [
                        INDUSTRIAL_AVERAGE_USAGE.PEAK_RAM_UTILIZATION_PCT.toString(), serverStatistics.peakRamUtilization.toString()
                    ])}
                </Box>
            )}
        </SpaceBetween>
    );

    return (
        <Alert
            data-testid='negative-savings-alert'
            type='info'
            header={constants.NEGATIVE_SAVING_HEADER}
            visible={visible}
        >
            {content}
        </Alert>
    );
}

export default NegativeSavingsAlert;
